/**
 * interpolate.ts — Template variable substitution for F6 {{interpolation}}.
 *
 * PURE MODULE: no React, no DOM, no window imports.
 *
 * Exported:
 *   interpolate(text, ctx)     — for plain text fields rendered by React.
 *   interpolateHtml(html, ctx) — for dangerouslySetInnerHTML fields (values HTML-escaped).
 *
 * Contract:
 *   - Single pass: parses all {{varName}} patterns, resolves each via resolveVariable.
 *   - Non-recursive: a resolved value that contains {{y}} is NOT re-scanned.
 *   - Fail-open: unknown / unset variable → empty string (no throw, no literal {{var}}).
 *   - D7 enforcement: URL fields must NOT be passed to either function — caller's duty.
 *
 * XSS-escape approach (interpolateHtml):
 *   The HTML TEMPLATE (block.html authored by quiz creator) is TRUSTED and passes through
 *   unchanged. Only the SUBSTITUTED VALUES (user answers, URL params) are HTML-escaped
 *   before insertion. This prevents XSS from untrusted input while preserving author HTML.
 *
 *   htmlEscape covers: & < > " '  (sufficient to break all known HTML injection vectors).
 *
 *   For plain text fields (title, paragraph, etc.), React renders the interpolated string
 *   as a text node — React's reconciler already escapes special characters in text nodes,
 *   so no extra escaping is needed in interpolate().
 */

import { resolveVariable, type VariableContext } from './variable-context';

/** Regex: matches {{anything}} — non-greedy to handle multiple vars per string. */
const VAR_PATTERN = /\{\{([^}]+)\}\}/g;

/**
 * Escape special HTML characters in an untrusted value before inserting into HTML.
 * Applied only to substituted VALUES in interpolateHtml, never to the template itself.
 */
function htmlEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Dangerous URI schemes that survive htmlEscape (no HTML-special chars) and
 * would execute if a creator places {{var}} inside an href/src attribute of an
 * HTML block template. htmlEscape only covers HTML character context, not URI
 * scheme context — so blank scheme-injecting values conservatively across the
 * whole HTML path (WEB-171 security-review HIGH).
 */
const UNSAFE_URL_SCHEME = /^[\s\u0000]*(?:javascript|data|vbscript):/i;

function safeUrlValue(value: string): string {
  return UNSAFE_URL_SCHEME.test(value) ? '' : value;
}

/**
 * interpolate — substitute {{var}} in a plain text template.
 *
 * Use for: title, sub_title, heading, paragraph, content labels, buttonText, etc.
 * React renders the result as a text node — no extra escaping needed.
 *
 * Values are NOT HTML-escaped (would cause double-encoding in text nodes).
 */
export function interpolate(text: string, ctx: VariableContext): string {
  return text.replace(VAR_PATTERN, (_, varName: string) =>
    resolveVariable(varName.trim(), ctx),
  );
}

/**
 * interpolateHtml — substitute {{var}} in an HTML template for dangerouslySetInnerHTML.
 *
 * Use for: html block (block.html) and any other field passed to dangerouslySetInnerHTML.
 *
 * 🔴 XSS-INVARIANT: Substituted VALUES are HTML-escaped before insertion.
 *   '<script>alert(1)</script>' in an answer → '&lt;script&gt;alert(1)&lt;/script&gt;'
 *   Template HTML (author-controlled <strong>, <p>, etc.) passes through unchanged.
 */
export function interpolateHtml(html: string, ctx: VariableContext): string {
  return html.replace(VAR_PATTERN, (_, varName: string) =>
    htmlEscape(safeUrlValue(resolveVariable(varName.trim(), ctx))),
  );
}
